import { ReactElement, useEffect } from 'react';
import {
  Navigate, Outlet, Routes, useLocation, useOutletContext,
} from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import SessionTimeoutAlert from '../../components/Modal/SessionTimeoutAlert';
import useLoginRedirect from '../LoginRedirect/useLoginRedirect';
import {
  useAuth, useIsAuth, useIsMsalAuth, useIsUserAuth, useStickySafeNavigate,
  ModalProvider,
  useSessionContext,
} from '../../..';

export function Router({ children }: { children: ReactElement<typeof Routes> }) {
  return (<>{children}</>);
}

export function AuthTypeRoutes({
  DefaultComponent,
  AuthComponent,
  MsalComponent,
}: {
  DefaultComponent?: ReactElement,
  AuthComponent?: ReactElement,
  MsalComponent?: ReactElement
}): ReactElement {
  const isUserAuth = useIsUserAuth();
  const isMsalAuth = useIsMsalAuth();

  if (isUserAuth && AuthComponent) {
    return AuthComponent;
  }
  if (isMsalAuth && MsalComponent) {
    return MsalComponent;
  }

  if (DefaultComponent) {
    return DefaultComponent;
  }

  return <Outlet />;
}

export function AuthRoutes<RoutingContextType>({ loginPath }: { loginPath: string }) {
  const outletValue: RoutingContextType = useOutletContext();
  const isAuth = useIsAuth();
  const { validate, refresh } = useAuth();
  const { inProgress } = useMsal();
  const { getIsLoggedOut } = useSessionContext();

  const navigate = useStickySafeNavigate();

  const location = useLocation();
  const { setLoginRedirect } = useLoginRedirect();

  useEffect(() => {
    (async () => {
      if (inProgress === InteractionStatus.None) {
        if (!isAuth) {
          try {
            // Using isLoggedOut localstorage flag as a cache.
            // This will reduce unnecessary calls to validate endpoint.
            if (getIsLoggedOut()) {
              throw new Error('User has already logged out');
            }

            const isExternalUser = await validate();
            if (!isExternalUser) {
              throw new Error('User\'s token is not valid');
            }

            await refresh();
          } catch (e) {
            setLoginRedirect(location.pathname);
            navigate(loginPath);
          }
        } else {
          setLoginRedirect();
        }
      }
    })();
  }, [isAuth, loginPath, inProgress]);

  if (!isAuth) {
    return <></>;
  }

  return (
    <>
      <ModalProvider>
        <SessionTimeoutAlert />
      </ModalProvider>
      <Outlet context={outletValue} />
    </>
  );
}

export function MsalRoutes({ badRequestPath }: { badRequestPath: string }) {
  const isMsalAuth = useIsMsalAuth();
  return isMsalAuth ? <Outlet /> : <Navigate to={badRequestPath} />;
}
